import React, { useState, useRef, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useProducts } from 'contexts/ProductsContext';
import { useCart } from 'contexts/CartContext';
import { useSizePreference } from 'contexts/SizePreference';
import { getColorHex, sortColors } from 'lib/colors';
import logoMain from 'assets/logo_main.png';

import musicbg from 'assets/bgs/musicbg.jpg';
import gamesbg from 'assets/bgs/gamesbg.jpg';
import memesbg from 'assets/bgs/memesbg.jpg';
import mm0bg from 'assets/bgs/mm0bg.jpg';
import mm1bg from 'assets/bgs/mm1bg.jpg';
import mm2bg from 'assets/bgs/mm2bg.jpg';
import mm3bg from 'assets/bgs/mm3bg.jpg';
import overlaygrit from 'assets/bgs/overlaygrit1.jpg';
import logosmix from 'assets/logosmix.png';
import backIcon from 'assets/windowsIcons/back.png';

import carousel1 from 'assets/carousel/herewego.jpg';
import carousel2 from 'assets/carousel/balls.jpg';
import carousel3 from 'assets/carousel/boonk.jpg';
import carousel4 from 'assets/carousel/granny.jpg';
import carousel5 from 'assets/carousel/princess.jpg';
import carousel6 from 'assets/carousel/rookie.jpg';

const carouselItems = [
  { img: carousel1, match: 'herewego', productName: 'Mike Wazowski' },
  { img: carousel2, match: 'balls', productName: 'Mister Balls' },
  { img: carousel3, match: 'boonk', productName: 'Cursed Emoji' },
  { img: carousel4, match: 'granny', productName: 'Minesweeper cat' },
  { img: carousel5, match: 'princess', productName: 'Evil kitty princess' },
  { img: carousel6, match: 'rookie', productName: 'Halo 3 ODST' },
];
const carouselImages = carouselItems.map(c => c.img);

const homeBgs = [mm0bg, mm1bg, mm2bg, mm3bg];
const categoryBgs = {
  music: musicbg,
  games: gamesbg,
  goofy: memesbg,
};

const categories = [
  { key: 'all', label: 'HOME' },
  { key: 'music', label: 'BANDS' },
  { key: 'games', label: 'GAMES' },
  // { key: 'rep', label: 'REP' }, // hidden until content is ready
  { key: 'goofy', label: 'GOOFY' },
];

function getBrandName(product) {
  if (typeof product === 'string') return product.replace(/\s*Oversized Tee$/i, '').trim();
  // Use band/game tag if available, fallback to parsing title
  if (product.band) return product.band;
  if (product.game) return product.game;
  if (product.brand) return product.brand;
  return product.name.replace(/\s*Oversized Tee$/i, '').trim();
}

// Thumbnail images for a card, honouring a single active colour filter.
// activeColor falsy (or not stocked by the product) → the product's default images.
function cardColor(product, activeColor) {
  return activeColor && product.colors && product.colors.includes(activeColor) ? activeColor : null;
}
function cardFront(product, activeColor) {
  const c = cardColor(product, activeColor);
  return (c && product.colorImages && product.colorImages[c] && product.colorImages[c][0]) || product.images[0];
}
function cardBack(product, activeColor) {
  const c = cardColor(product, activeColor) || product.defaultColor;
  return c && ((product.colorBackImages && product.colorBackImages[c]) || (product.colorImages && product.colorImages[c] && product.colorImages[c][1]));
}

/* ── Category View (Plex-style) ── */
function CategoryView({ products, categoryLabel, onOpenProduct, dropdownOpen, setDropdownOpen, brandFilters, setBrandFilters, genreFilters, setGenreFilters, activeColor }) {
  const scrollRef = useRef(null);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'alpha' or 'grid'
  const [genreDropdownOpen, setGenreDropdownOpen] = useState(false);

  // Get unique genres from this category's products
  const availableGenres = [...new Set(products.filter(p => p.genre).map(p => p.genre))].sort();
  const hasGenres = availableGenres.length > 0;

  const sorted = [...products]
    .map(p => ({ ...p, brand: getBrandName(p) }))
    .sort((a, b) => a.brand.localeCompare(b.brand));

  const grouped = {};
  sorted.forEach(p => {
    const letter = p.brand[0].toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(p);
  });

  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const activeLetters = Object.keys(grouped).sort();

  function scrollToLetter(letter) {
    if (!scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-letter="${letter}"]`)
      || scrollRef.current.querySelector(`[data-brand-letter="${letter}"]`);
    if (el) {
      const container = scrollRef.current;
      const targetTop = el.offsetTop - container.offsetTop;
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  }

  // Filter by genre(s) then brand(s) then tab
  const genreFiltered = genreFilters.length > 0
    ? sorted.filter(p => p.genre && genreFilters.includes(p.genre))
    : sorted;
  const brandFiltered = brandFilters.length > 0
    ? genreFiltered.filter(p => brandFilters.includes(p.brand))
    : genreFiltered;
  const displayProducts = activeTab === 'all'
    ? brandFiltered
    : activeTab === 'recent'
    ? [...brandFiltered].reverse().slice(0, 8)
    : activeTab === 'onoffer'
    ? brandFiltered.filter(p => p.compareAtPrice && p.compareAtPrice > p.price)
    : brandFiltered;

  const featuredProducts = displayProducts.filter(p => p.isFeatured);
  const nonFeaturedProducts = displayProducts.filter(p => !p.isFeatured);

  const displayGrouped = {};
  nonFeaturedProducts.forEach(p => {
    const letter = p.brand[0].toUpperCase();
    if (!displayGrouped[letter]) displayGrouped[letter] = [];
    displayGrouped[letter].push(p);
  });
  const displayLetters = Object.keys(displayGrouped).sort();

  return (
    <div className="cat-view">
      {/* Top bar: dropdown + tabs */}
      <div className="cat-view__topbar">
        <div className="cat-view__dropdown-wrap">
          {sorted.some(p => p.band || p.game) ? (
          <button
            className="cat-view__dropdown-btn"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {categoryLabel} ▾
          </button>
          ) : (
            <span className="cat-view__dropdown-btn" style={{ cursor: 'default' }}>
              {categoryLabel}
            </span>
          )}
          {dropdownOpen && (
            <div className="cat-view__dropdown-panel">
              <div className="cat-view__dropdown-header">ALL {categoryLabel.toUpperCase()}</div>
              <div className="cat-view__dropdown-tags">
                {[...new Set(sorted.map(p => p.brand))].map(brand => {
                  const count = sorted.filter(p => p.brand === brand).length;
                  return (
                  <button
                    key={brand}
                    className={`cat-view__dropdown-tag ${brandFilters.includes(brand) ? 'cat-view__dropdown-tag--active' : ''}`}
                    onClick={() => {
                      setBrandFilters(prev => {
                        if (prev.includes(brand)) return prev.filter(b => b !== brand);
                        if (prev.length >= 4) return prev;
                        return [...prev, brand];
                      });
                    }}
                  >
                    {brand}{` (${count})`}
                  </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {brandFilters.length > 0 && (
          <div className="cat-view__active-filters">
            {brandFilters.map(brand => (
              <span key={brand} className="cat-view__filter-tag">
                {brand}
                <button
                  className="cat-view__filter-tag-remove"
                  onClick={() => setBrandFilters(prev => prev.filter(b => b !== brand))}
                >
                  ✕
                </button>
              </span>
            ))}
            <button className="cat-view__filter-clear" onClick={() => setBrandFilters([])}>
              Clear all
            </button>
          </div>
        )}
        <span className="cat-view__count">{displayProducts.length}</span>
        <div className="cat-view__tabs">
          <button
            className={`cat-view__tab ${activeTab === 'onoffer' ? 'cat-view__tab--active' : ''}`}
            onClick={() => setActiveTab('onoffer')}
          >
            On Offer
          </button>
          <button
            className={`cat-view__tab ${activeTab === 'all' ? 'cat-view__tab--active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
        </div>
        <div className="cat-view__viewtoggle">
          <button
            className={`cat-view__viewbtn ${viewMode === 'alpha' ? 'cat-view__viewbtn--active' : ''}`}
            onClick={() => setViewMode('alpha')}
            title="Alphabet List"
          >
            ☰
          </button>
          <button
            className={`cat-view__viewbtn ${viewMode === 'grid' ? 'cat-view__viewbtn--active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid"
          >
            ⊞
          </button>
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {(dropdownOpen || genreDropdownOpen) && (
        <div className="cat-view__overlay" onClick={() => { setDropdownOpen(false); setGenreDropdownOpen(false); }} />
      )}

      <div className="cat-view__body">
        <div className="cat-view__scroll" ref={scrollRef}>
          {featuredProducts.length > 0 && (
            <div className="cat-view__section">
              <div className="cat-view__section-label">★ Featured</div>
              <div className="cat-view__grid">
                {featuredProducts.map(product => (
                  <div
                    key={product.id}
                    className="cat-view__card"
                    onClick={() => onOpenProduct && onOpenProduct(product)}
                  >
                    <div className={`cat-view__card-image ${product.isDualPrint ? 'cat-view__card-image--dual' : ''}`}>
                      <img className="cat-view__img-front" src={cardFront(product, activeColor)} alt={product.name} />
                      {product.isDualPrint && (() => {
                        const dc = cardColor(product, activeColor) || product.defaultColor;
                        const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
                        return backImg ? <img className="cat-view__img-back" src={backImg} alt={product.name + ' back'} /> : null;
                      })()}
                    </div>
                    <div className="cat-view__card-name">{product.name}</div>
                    <div className="cat-view__card-price">{'\u20B9'}{product.price.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {viewMode === 'alpha' ? (
            /* Alphabet-sectioned view */
            displayLetters.map(letter => (
              <div key={letter} data-letter={letter} className="cat-view__section">
                <div className="cat-view__section-label">{letter}</div>
                <div className="cat-view__grid">
                  {displayGrouped[letter].map(product => (
                    <div
                      key={product.id}
                      className="cat-view__card"
                      onClick={() => onOpenProduct && onOpenProduct(product)}
                    >
                      <div className={`cat-view__card-image ${product.isDualPrint ? 'cat-view__card-image--dual' : ''}`}>
                        <img className="cat-view__img-front" src={cardFront(product, activeColor)} alt={product.name} />
                        {product.isDualPrint && (() => {
                          const dc = cardColor(product, activeColor) || product.defaultColor;
                          const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
                          return backImg ? <img className="cat-view__img-back" src={backImg} alt={product.name + ' back'} /> : null;
                        })()}
                      </div>
                      <div className="cat-view__card-name">{product.name}</div>
                      <div className="cat-view__card-price">{'\u20B9'}{product.price.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            /* Flat grid view */
            <div className="cat-view__flatgrid">
              {displayProducts.map((product, i) => {
                const letter = product.brand[0].toUpperCase();
                const isFirst = i === 0 || displayProducts[i - 1].brand[0].toUpperCase() !== letter;
                return (
                  <div
                    key={product.id}
                    className="cat-view__card"
                    onClick={() => onOpenProduct && onOpenProduct(product)}
                    {...(isFirst ? { 'data-brand-letter': letter } : {})}
                  >
                    <div className={`cat-view__card-image ${product.isDualPrint ? 'cat-view__card-image--dual' : ''}`}>
                      <img className="cat-view__img-front" src={cardFront(product, activeColor)} alt={product.name} />
                      {product.isDualPrint && (() => {
                        const dc = cardColor(product, activeColor) || product.defaultColor;
                        const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
                        return backImg ? <img className="cat-view__img-back" src={backImg} alt={product.name + ' back'} /> : null;
                      })()}
                    </div>
                    <div className="cat-view__card-name">{product.name}</div>
                    <div className="cat-view__card-price">{'\u20B9'}{product.price.toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Alphabet rail on right */}
        <div className="cat-view__alpha">
          {['#', ...allLetters].map(letter => {
            const has = letter === '#' ? false : !!grouped[letter];
            return (
              <button
                key={letter}
                className={`cat-view__alpha-letter ${!has ? 'cat-view__alpha-letter--empty' : ''}`}
                onClick={() => has && scrollToLetter(letter)}
                disabled={!has}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Main Binco Component ── */
function FeaturedSlideshow({ products, onOpenProduct, activeColor }) {
  const containerRef = React.useRef(null);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    function measure() {
      if (!containerRef.current) return;
      setContainerWidth(containerRef.current.offsetWidth);
    }
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const minCard = 160;
  const gap = 20;
  const itemsPerPage = Math.max(1, Math.floor((containerWidth + gap) / (minCard + gap)));
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const needsSlideshow = totalPages > 1;
  const safePage = currentPage % totalPages;

  const [paused, setPaused] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [isSliding, setIsSliding] = React.useState(false);
  const dragRef = React.useRef({ dragging: false, startX: 0 });

  // Reset page when layout changes
  React.useEffect(() => {
    setCurrentPage(0);
    setProgress(0);
  }, [itemsPerPage]);

  // Auto-advance with progress
  React.useEffect(() => {
    if (!needsSlideshow || paused) return;
    const interval = 50;
    const duration = 4000;
    const step = (interval / duration) * 100;
    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          setIsSliding(true);
          setTimeout(() => {
            setCurrentPage(prev => (prev + 1) % totalPages);
            setIsSliding(false);
          }, 400);
          return 0;
        }
        return p + step;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [totalPages, needsSlideshow, paused]);

  const nextPage = (safePage + 1) % totalPages;
  const gridCols = `repeat(${itemsPerPage}, 1fr)`;

  function slideNext() {
    if (isSliding) return;
    setIsSliding(true);
    setProgress(0);
    setTimeout(() => { setCurrentPage(prev => (prev + 1) % totalPages); setIsSliding(false); }, 400);
  }
  function slidePrev() {
    if (isSliding) return;
    setIsSliding(true);
    setProgress(0);
    setTimeout(() => { setCurrentPage(prev => (prev - 1 + totalPages) % totalPages); setIsSliding(false); }, 400);
  }

  function onDragStart(e) {
    dragRef.current = { dragging: true, startX: e.clientX };
  }
  function onDragEnd(e) {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    dragRef.current.dragging = false;
    if (Math.abs(dx) > 50) {
      dragRef.current.didDrag = true;
      if (dx < 0) slideNext();
      else slidePrev();
      setTimeout(() => { dragRef.current.didDrag = false; }, 50);
    }
  }

  function goToPage(i) {
    if (i === safePage || isSliding) return;
    setIsSliding(true);
    setProgress(0);
    setTimeout(() => {
      setCurrentPage(i);
      setIsSliding(false);
    }, 400);
  }

  const renderPage = (pageIdx) => {
    const start = pageIdx * itemsPerPage;
    const items = products.slice(start, start + itemsPerPage);
    return items.map(product => (
      <div
        key={product.id}
        className="product-card"
        onClick={() => { if (dragRef.current.didDrag) return; onOpenProduct && onOpenProduct(product); }}
      >
        <div className={`product-card__image ${product.isDualPrint ? 'product-card__image--dual' : ''}`}>
          <img className="product-card__img-front" src={cardFront(product, activeColor)} alt={product.name} />
          {product.isDualPrint && (() => {
            const dc = cardColor(product, activeColor) || product.defaultColor;
            const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
            return backImg ? <img className="product-card__img-back" src={backImg} alt={product.name + ' back'} /> : null;
          })()}
        </div>
        <a href="#!" className="product-card__name">{product.name}</a>
        <div className="product-card__price">{'\u20B9'}{product.price.toLocaleString()}</div>
      </div>
    ));
  };

  return (
    <div className="featured-slideshow" ref={containerRef} onMouseEnter={() => setPaused(true)} onMouseLeave={(e) => { setPaused(false); onDragEnd(e); }} onMouseDown={onDragStart} onMouseUp={onDragEnd} onDragStart={e => e.preventDefault()} style={{ cursor: needsSlideshow ? 'grab' : undefined }}>
      {needsSlideshow && <button className="featured-slideshow__arrow featured-slideshow__arrow--left" onClick={slidePrev}>◀</button>}
      {needsSlideshow && <button className="featured-slideshow__arrow featured-slideshow__arrow--right" onClick={slideNext}>▶</button>}
      <div className={`featured-slideshow__slider ${isSliding ? 'featured-slideshow__slider--sliding' : ''}`}>
        <div className="featured-slideshow__page" style={{ gridTemplateColumns: gridCols }}>
          {renderPage(safePage)}
        </div>
        <div className="featured-slideshow__page" style={{ gridTemplateColumns: gridCols }}>
          {renderPage(nextPage)}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="featured-slideshow__indicators">
          {Array.from({ length: totalPages }).map((_, i) => (
            <div
              key={i}
              className="featured-slideshow__dash"
              onClick={() => goToPage(i)}
            >
              <div
                className="featured-slideshow__dash-fill"
                style={{ width: i === safePage ? `${progress}%` : i < safePage ? '100%' : '0%' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarouselStrip({ images, height, speed = 40, onClickSlide }) {
  const trackRef = React.useRef(null);
  const [offset, setOffset] = React.useState(0);
  const [firstHalfWidth, setFirstHalfWidth] = React.useState(0);
  const [loadedCount, setLoadedCount] = React.useState(0);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || loadedCount < images.length) return;
    const slides = track.querySelectorAll('.cs__slide');
    const half = images.length;
    let w = 0;
    for (let i = 0; i < half && i < slides.length; i++) {
      w += slides[i].offsetWidth;
    }
    if (w > 0) setFirstHalfWidth(w);
  }, [images, loadedCount]);

  React.useEffect(() => {
    if (!firstHalfWidth) return;
    let raf;
    let last = performance.now();
    let pos = offset;
    function tick(now) {
      const dt = (now - last) / 1000;
      last = now;
      pos -= speed * dt;
      if (pos <= -firstHalfWidth) pos += firstHalfWidth;
      setOffset(pos);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [firstHalfWidth, speed]);

  return (
    <div className="binco-carousel">
      <div
        ref={trackRef}
        className="binco-carousel__track"
        style={{ transform: `translateX(${offset}px)` }}
      >
        {[...images, ...images, ...images].map((img, i) => (
          <div key={i} className="cs__slide binco-carousel__slide" onClick={() => onClickSlide && onClickSlide(i % images.length)} style={{ cursor: onClickSlide ? 'pointer' : 'default' }}>
            <img src={img} alt="" onLoad={i < images.length ? () => setLoadedCount(c => c + 1) : undefined} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ColorFilter({ availableColors, colorFilters, setColorFilters }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function toggle(color) {
    setColorFilters(prev =>
      prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color],
    );
  }

  const label =
    colorFilters.length === 0
      ? 'Colour'
      : colorFilters.length === 1
        ? colorFilters[0]
        : `${colorFilters.length} colours`;

  return (
    <div className="binco-color-filter" ref={ref}>
      <button
        type="button"
        className={`binco-header__size-pref binco-color-filter__trigger ${colorFilters.length ? 'binco-header__size-pref--active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {colorFilters.length > 0 && (
          <span className="binco-color-filter__dots">
            {colorFilters.slice(0, 4).map(c => (
              <span key={c} className="binco-color-filter__dot" style={{ background: getColorHex(c) }} />
            ))}
          </span>
        )}
        <span className="binco-color-filter__label">{label}</span>
        <span className="binco-color-filter__caret">▾</span>
      </button>
      {open && (
        <div className="binco-color-filter__menu">
          {colorFilters.length > 0 && (
            <button type="button" className="binco-color-filter__clear" onClick={() => setColorFilters([])}>
              Clear ({colorFilters.length})
            </button>
          )}
          {availableColors.map(c => {
            const active = colorFilters.includes(c);
            return (
              <button
                type="button"
                key={c}
                className={`binco-color-filter__option ${active ? 'binco-color-filter__option--active' : ''}`}
                onClick={() => toggle(c)}
              >
                <span className="binco-color-filter__swatch" style={{ background: getColorHex(c) }} />
                <span className="binco-color-filter__name">{c}</span>
                {active && <span className="binco-color-filter__check">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Binco({ onClose, onOpenProduct, onOpenCart, sharedCategory, sharedBrand }) {
  const { products, loading } = useProducts();
  const { getItemCount } = useCart();
  const { sizePreference, setSizePreference } = useSizePreference();
  const [activeCategory, setActiveCategory] = useState(sharedCategory || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [brandFilters, setBrandFilters] = useState(sharedBrand ? [sharedBrand] : []);
  const [genreFilters, setGenreFilters] = useState([]);
  const [colorFilters, setColorFilters] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [featuredCollapsed, setFeaturedCollapsed] = useState(false);
  const [navHistory, setNavHistory] = useState([]);

  function pushNav() {
    setNavHistory(prev => [...prev, {
      activeCategory, brandFilters: [...brandFilters], genreFilters: [...genreFilters], searchQuery, searchFocused,
    }]);
  }
  function navigateBack() {
    if (navHistory.length === 0) return;
    const prev = navHistory[navHistory.length - 1];
    setNavHistory(h => h.slice(0, -1));
    setActiveCategory(prev.activeCategory);
    setBrandFilters(prev.brandFilters);
    setGenreFilters(prev.genreFilters);
    setSearchQuery(prev.searchQuery);
    setSearchFocused(prev.searchFocused);
  }
  const canGoBack = navHistory.length > 0;
  const [sortMode, setSortMode] = useState('new'); // 'new', 'name', 'name-desc', 'random'
  const [randomSeed, setRandomSeed] = useState(0);

  // Pick a random home bg once per mount
  const homeBg = useMemo(() => homeBgs[Math.floor(Math.random() * homeBgs.length)], []);

  // Listen for category navigation from ProductViewer
  useEffect(() => {
    function onNavigate(e) {
      const { category, brand } = e.detail;
      pushNav();
      setActiveCategory(category);
      setBrandFilters(brand ? [brand] : []);
      setGenreFilters([]);
      setSearchQuery('');
      setSearchFocused(false);
    }
    window.addEventListener('binco-navigate', onNavigate);
    return () => window.removeEventListener('binco-navigate', onNavigate);
  }, [activeCategory, brandFilters, genreFilters, searchQuery, searchFocused]);

  const currentBg = activeCategory === 'all'
    ? homeBg
    : categoryBgs[activeCategory] || null;

  const availableColors = useMemo(
    () => sortColors([...new Set(products.flatMap(p => p.colors || []))]),
    [products],
  );

  const filtered = products.filter(p => {
    const matchesCategory =
      activeCategory === 'all' || (p.categories ? p.categories.includes(activeCategory) : p.category === activeCategory);
    const matchesSearch = p.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesColor = colorFilters.length === 0 || (p.colors && p.colors.some(c => colorFilters.includes(c)));
    return matchesCategory && matchesSearch && matchesColor;
  });


  const activeCatLabel = categories.find(c => c.key === activeCategory)?.label || 'ALL';

  // When exactly one colour is filtered, use it for thumbnails and product pre-selection.
  const activeColor = colorFilters.length === 1 ? colorFilters[0] : null;

  function openProduct(product, selections) {
    let sel = selections || {};
    if (!sel.color && colorFilters.length === 1 && product.colors && product.colors.includes(colorFilters[0])) {
      sel = { ...sel, color: colorFilters[0] };
    }
    onOpenProduct && onOpenProduct(product, sel);
  }

  return (
    <BincoDiv>
      <header className="binco-header">
        <div className="binco-header__brand">
          <img className="binco-header__logo-img" src={logoMain} alt="binco" style={{ cursor: 'pointer' }} onClick={() => { pushNav(); setActiveCategory('all'); setBrandFilters([]); setGenreFilters([]); setColorFilters([]); setSearchQuery(''); }} />
        </div>
        <div className="binco-header__search">
          <button className={`binco-header__back ${canGoBack ? '' : 'binco-header__back--disabled'}`} onClick={navigateBack} disabled={!canGoBack}>
            <img src={backIcon} alt="Back" />
          </button>
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); if (e.target.value.length > 0) setSearchFocused(true); }}
            onFocus={() => { if (searchQuery.length > 0) setSearchFocused(true); }}
          />
          {searchQuery.length > 0 && (
            <button className="binco-search-clear" onClick={() => { setSearchQuery(''); setSearchFocused(false); }}>✕</button>
          )}
        </div>
        <nav className="binco-header__nav">
          <ColorFilter
            availableColors={availableColors}
            colorFilters={colorFilters}
            setColorFilters={setColorFilters}
          />
          <select
            className="binco-header__size-pref"
            value={sizePreference}
            onChange={e => setSizePreference(e.target.value)}
          >
            <option value="">My Size</option>
            {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className={`binco-header__cart-btn ${getItemCount() > 0 ? 'binco-header__cart-btn--glow' : ''}`} onClick={() => onOpenCart && onOpenCart()}>
            🛒 Cart{getItemCount() > 0 ? ` (${getItemCount()})` : ''}
          </button>
          <button className="binco-header__account-btn" onClick={() => window.open('https://shopify.com/99290480957/account/profile', '_blank')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </button>
        </nav>
      </header>

      {/* Search Overlay */}
      {searchFocused && searchQuery.length > 0 && (() => {
        const q = searchQuery.toLowerCase();
        const catLabel = (cat) => cat === 'music' ? 'BANDS' : cat.toUpperCase();
        const brandMatches = [];
        const seen = new Set();
        products.forEach(p => {
          if (p.category === 'goofy') return;
          const brand = getBrandName(p);
          if (brand.toLowerCase().includes(q) && !seen.has(brand)) {
            seen.add(brand);
            const count = products.filter(pp => getBrandName(pp) === brand).length;
            brandMatches.push({ brand, category: p.category, count });
          }
        });
        const productMatches = products.filter(p => p.name.toLowerCase().includes(q));
        return (
          <div className="binco-search-overlay">
            <div className="binco-search-overlay__bg" style={{ backgroundImage: `url(${homeBg})` }} onClick={() => { setSearchQuery(''); setSearchFocused(false); }} />
            <div className="binco-search-overlay__content">
              <div className="binco-search-overlay__scroll">
                {brandMatches.length > 0 && (
                  <>
                    <div className="binco-search-overlay__section">BRANDS / GAMES</div>
                    <div className="binco-search-overlay__brands">
                      {brandMatches.map(({ brand, category, count }) => (
                        <button
                          key={`brand-${brand}`}
                          className="binco-search-overlay__brand-tag"
                          onClick={() => {
                            pushNav();
                            setActiveCategory(category);
                            setBrandFilters([brand]);
                            setSearchQuery('');
                            setSearchFocused(false);
                          }}
                        >
                          {brand} ({count}) <span className="binco-search-overlay__cat">{catLabel(category)}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {productMatches.length > 0 && (
                  <>
                    <div className="binco-search-overlay__section">PRODUCTS ({productMatches.length})</div>
                    <div className="binco-search-overlay__grid">
                      {productMatches.map(product => (
                        <div
                          key={product.id}
                          className="product-card"
                          onClick={() => openProduct(product)}
                        >
                          <div className={`product-card__image ${product.isDualPrint ? 'product-card__image--dual' : ''}`}>
                            <img className="product-card__img-front" src={cardFront(product, activeColor)} alt={product.name} />
                            {product.isDualPrint && (() => {
                              const dc = cardColor(product, activeColor) || product.defaultColor;
                              const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
                              return backImg ? <img className="product-card__img-back" src={backImg} alt={product.name + ' back'} /> : null;
                            })()}
                          </div>
                          <a href="#!" className="product-card__name">{product.name}</a>
                          <div className="product-card__price">{'\u20B9'}{product.price.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {brandMatches.length === 0 && productMatches.length === 0 && (
                  <div className="binco-search-overlay__empty">No results found</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="binco-body">
        <aside className="binco-sidebar">
          {categories.filter(c => c.key === 'all').map(cat => (
            <div key={cat.key} className="binco-sidebar__item-wrap">
              <button
                className={`binco-sidebar__item ${activeCategory === 'all' ? 'binco-sidebar__item--active' : ''}`}
                onClick={() => { pushNav(); setActiveCategory('all'); setBrandFilters([]); setGenreFilters([]); }}
              >
                {cat.label}
              </button>
            </div>
          ))}
          <div className="binco-sidebar__heading">Browse by Category</div>
          {categories.filter(c => c.key !== 'all').map(cat => {
            const hasSubmenu = cat.key === 'music' || cat.key === 'games';
            const subItems = hasSubmenu
              ? products
                  .filter(p => p.categories ? p.categories.includes(cat.key) : p.category === cat.key)
                  .map(p => ({ brand: getBrandName(p), product: p }))
                  .sort((a, b) => a.brand.localeCompare(b.brand))
              : [];
            return (
              <div
                key={cat.key}
                className="binco-sidebar__item-wrap"
                onMouseEnter={() => hasSubmenu && !catDropdownOpen && setHoveredCategory(cat.key)}
                onMouseLeave={() => setHoveredCategory(null)}
              >
                <button
                  className={`binco-sidebar__item ${
                    activeCategory === cat.key ? 'binco-sidebar__item--active' : ''
                  }`}
                  onClick={() => { pushNav(); setActiveCategory(cat.key); setBrandFilters([]); setGenreFilters([]); }}
                >
                  {cat.label}
                  {hasSubmenu && <span className="binco-sidebar__arrow">▶</span>}
                </button>
                {hasSubmenu && hoveredCategory === cat.key && (() => {
                  const brands = [...new Set(subItems.map(s => s.brand))];
                  const items = [];
                  let lastLetter = null;
                  brands.forEach(brand => {
                    const first = brand.charAt(0).toUpperCase();
                    const letter = /[A-Z]/.test(first) ? first : '#';
                    if (letter !== lastLetter) {
                      items.push({ type: 'header', letter });
                      lastLetter = letter;
                    }
                    items.push({ type: 'band', name: brand, count: subItems.filter(s => s.brand === brand).length });
                  });
                  const PER_COL = 10;
                  const cols = [];
                  let i = 0;
                  let carryLetter = null;
                  while (i < items.length) {
                    let take = Math.min(PER_COL, items.length - i);
                    if (take > 0 && items[i + take - 1].type === 'header' && i + take < items.length) {
                      take -= 1;
                    }
                    const colItems = items.slice(i, i + take);
                    if (carryLetter && colItems.length > 0 && colItems[0].type !== 'header') {
                      colItems.unshift({ type: 'header', letter: carryLetter });
                    }
                    cols.push(colItems);
                    const lastBand = [...colItems].reverse().find(it => it.type === 'band');
                    if (lastBand) {
                      const f = lastBand.name.charAt(0).toUpperCase();
                      carryLetter = /[A-Z]/.test(f) ? f : '#';
                    }
                    i += take;
                  }
                  return (
                  <div className={`binco-submenu ${cols.length > 1 ? 'binco-submenu--multi' : ''}`}>
                    {cols.map((col, ci) => (
                      <div key={ci} className="binco-submenu__col">
                        {col.map((item, ii) => {
                          if (item.type === 'header') {
                            return (
                              <div key={`h-${item.letter}-${ii}`} className="binco-submenu__letter">
                                {item.letter}
                              </div>
                            );
                          }
                          return (
                            <button
                              key={item.name}
                              className="binco-submenu__item"
                              onClick={() => {
                                pushNav();
                                setActiveCategory(cat.key);
                                setBrandFilters([item.name]);
                                setGenreFilters([]);
                                setHoveredCategory(null);
                              }}
                            >
                              {item.name}{` (${item.count})`}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            );
          })}
          <div className="binco-sidebar__help">
            Need help?<br />
            <a href="#!" onClick={(e) => { e.preventDefault(); const el = e.target; navigator.clipboard.writeText('support@binco67.in').then(() => { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'support@binco67.in'; }, 1500); }).catch(() => {}); }}>support@binco67.in</a>
            <br />
            <a href="/policies/faq.html" target="_blank">FAQs</a>
            <br />
            <a href="/policies/shipping-returns.html" target="_blank">Shipping &amp; Returns</a>
          </div>
        </aside>

        <main className="binco-content" style={currentBg ? { backgroundImage: `url(${currentBg})` } : {}}>
          {loading ? (
            <div className="binco-loading">
              <div className="binco-loading__bar"><div className="binco-loading__fill" /></div>
              <div className="binco-loading__text">Loading products...</div>
            </div>
          ) : activeCategory === 'all' ? (
            <>
              <div className="binco-content__banner">
                *NEW* - New Designs Now Available &nbsp;|&nbsp; Free shipping above {'\u20B9'}999
              </div>
              {filtered.filter(p => p.isFeaturedHome).length > 0 && (
                <>
                  <div className="binco-content__heading binco-content__heading--toggle" onClick={() => setFeaturedCollapsed(c => !c)}>
                    ★ Featured <span className="binco-content__collapse-arrow">{featuredCollapsed ? '▶' : '▼'}</span>
                  </div>
                  {!featuredCollapsed && (
                    <FeaturedSlideshow
                      products={filtered.filter(p => p.isFeaturedHome).sort((a, b) => a.featuredOrder - b.featuredOrder)}
                      onOpenProduct={openProduct}
                      activeColor={activeColor}
                    />
                  )}
                </>
              )}
              <div className="binco-content__heading binco-content__heading--sticky binco-content__heading--sortbar">
                <span>All Products</span>
                <div className="binco-sortbar">
                  <button className={`binco-sortbar__btn ${sortMode === 'new' ? 'binco-sortbar__btn--active' : ''}`} onClick={() => setSortMode('new')}>New</button>
                  <button className={`binco-sortbar__btn ${sortMode === 'name' || sortMode === 'name-desc' ? 'binco-sortbar__btn--active' : ''}`} onClick={() => setSortMode(s => s === 'name' ? 'name-desc' : 'name')}>
                    Name {sortMode === 'name' ? '↑' : sortMode === 'name-desc' ? '↓' : ''}
                  </button>
                  <button className="binco-sortbar__btn" onClick={() => { setSortMode('random'); setRandomSeed(Math.random()); }}>Shuffle</button>
                </div>
              </div>
              <div className="binco-content__grid">
                {(() => {
                  let sorted = [...filtered];
                  if (sortMode === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
                  else if (sortMode === 'name-desc') sorted.sort((a, b) => b.name.localeCompare(a.name));
                  else if (sortMode === 'random') {
                    for (let i = sorted.length - 1; i > 0; i--) {
                      const j = Math.floor((Math.sin(randomSeed * (i + 1) * 9301 + 49297) * 0.5 + 0.5) * (i + 1));
                      [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
                    }
                  }
                  else sorted.reverse(); // 'new' = reversed Shopify order
                  return sorted;
                })().map(product => (
                  <div
                    key={product.id}
                    className="product-card"
                    onClick={() => openProduct(product)}
                  >
                    <div className={`product-card__image ${product.isDualPrint ? 'product-card__image--dual' : ''}`}>
                      <img className="product-card__img-front" src={cardFront(product, activeColor)} alt={product.name} />
                      {product.isDualPrint && (() => {
                        const dc = cardColor(product, activeColor) || product.defaultColor;
                        const backImg = dc && ((product.colorBackImages && product.colorBackImages[dc]) || (product.colorImages && product.colorImages[dc]?.[1]));
                        return backImg ? <img className="product-card__img-back" src={backImg} alt={product.name + ' back'} /> : null;
                      })()}
                    </div>
                    <a href="#!" className="product-card__name">{product.name}</a>
                    <div className="product-card__price">{'\u20B9'}{product.price.toLocaleString()}</div>
                  </div>
                ))}
              </div>
              {filtered.length === 0 && (
                <div className="binco-content__empty">No products found.</div>
              )}
              <CarouselStrip images={carouselImages} height={220} speed={40} onClickSlide={(i) => {
                const item = carouselItems[i];
                if (item) {
                  const match = products.find(p => p.name.toLowerCase().includes(item.productName.toLowerCase()));
                  if (match) openProduct(match);
                }
              }} />
            </>
          ) : (
            <CategoryView
              products={filtered}
              categoryLabel={activeCatLabel}
              onOpenProduct={openProduct}
              activeColor={activeColor}
              dropdownOpen={catDropdownOpen}
              setDropdownOpen={setCatDropdownOpen}
              brandFilters={brandFilters}
              setBrandFilters={setBrandFilters}
              genreFilters={genreFilters}
              setGenreFilters={setGenreFilters}
            />
          )}
        </main>
        <div className="binco-payment-badges">
          <img src={logosmix} alt="Powered by Shopify & Razorpay" />
        </div>
      </div>

      <footer className="binco-statusbar">
        <div className="binco-statusbar__left">
          {filtered.length} item{filtered.length !== 1 ? 's' : ''}
          {activeCategory !== 'all' && ` in ${activeCategory}`}
        </div>
        <div className="binco-statusbar__right"><a href="/policies/privacy-policy.html" target="_blank" style={{ color: '#777', textDecoration: 'none' }}>Privacy Policy</a> &nbsp;|&nbsp; <a href="#!" style={{ color: '#777', textDecoration: 'none', cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); const el = e.target; navigator.clipboard.writeText('support@binco67.in').then(() => { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'support@binco67.in'; }, 1500); }).catch(() => {}); }}>support@binco67.in</a> &nbsp;|&nbsp; binco67.in</div>
      </footer>
    </BincoDiv>
  );
}

const BincoDiv = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Verdana, Tahoma, sans-serif;
  font-size: 11px;
  overflow: hidden;

  /* ── Header ── */
  .binco-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 14px;
    background: linear-gradient(180deg, #2d2d2d 0%, #1a1a1a 100%);
    border-bottom: 2px solid #f5a623;
    min-height: 44px;
    position: relative;
    z-index: 51;
  }
  .binco-header__back {
    background: none;
    border: 1px solid transparent;
    color: #888;
    cursor: pointer;
    padding: 3px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    img { height: 22px; width: auto; }
    &:hover { border-color: #555; background: rgba(255,255,255,0.05); }
    &:active { background: rgba(0,0,0,0.2); }
    &--disabled {
      opacity: 0.25;
      cursor: default;
      pointer-events: none;
    }
  }
  .binco-header__brand {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .binco-header__logo-img {
    height: 50px;
    width: auto;
    object-fit: contain;
  }
  .binco-header__search {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 20px;
    position: relative;
    input {
      width: 100%;
      max-width: 300px;
      background: #111;
      border: 1px solid #444;
      color: #ccc;
      padding: 5px 10px;
      font-size: 11px;
      font-family: Verdana, Tahoma, sans-serif;
      &:focus {
        border-color: #f5a623;
        outline: none;
      }
      &::placeholder { color: #666; }
    }
  }
  .binco-header__size-pref {
    background: #222;
    border: 1px solid #444;
    color: #ccc;
    font-size: 10px;
    font-family: Tahoma, sans-serif;
    padding: 4px 6px;
    cursor: pointer;
    &:focus { border-color: #f5a623; outline: none; }
    option { background: #222; color: #ccc; }
  }
  .binco-header__size-pref--active {
    border-color: #f5a623;
    color: #f5a623;
  }
  .binco-color-filter {
    position: relative;
    display: inline-flex;
  }
  .binco-color-filter__trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .binco-color-filter__dots {
    display: inline-flex;
    gap: 2px;
  }
  .binco-color-filter__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.4);
  }
  .binco-color-filter__label {
    text-transform: capitalize;
  }
  .binco-color-filter__caret {
    font-size: 8px;
    color: #888;
  }
  .binco-color-filter__menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 50;
    min-width: 150px;
    max-height: 320px;
    overflow-y: auto;
    padding: 3px;
    background: #222;
    border: 1px solid #555;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .binco-color-filter__clear {
    display: block;
    width: 100%;
    margin-bottom: 3px;
    padding: 4px;
    background: #333;
    border: 1px solid #444;
    color: #f5a623;
    font-size: 9px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    &:hover { background: #3a3a3a; }
  }
  .binco-color-filter__option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 5px 6px;
    background: transparent;
    border: none;
    color: #ccc;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    text-align: left;
    cursor: pointer;
    &:hover { background: #333; }
  }
  .binco-color-filter__option--active {
    background: #2c2c2c;
    color: #f5a623;
  }
  .binco-color-filter__swatch {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 2px;
  }
  .binco-color-filter__name {
    flex: 1;
    text-transform: capitalize;
  }
  .binco-color-filter__check {
    font-size: 10px;
    color: #f5a623;
  }
  .binco-header__cart-btn {
    background: linear-gradient(180deg, #3a3a3a 0%, #222 100%);
    border: 1px solid #555;
    color: #f5a623;
    font-size: 11px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    padding: 4px 14px;
    cursor: pointer;
    box-shadow: inset 1px 1px rgba(255,255,255,0.08), 1px 1px 2px rgba(0,0,0,0.3);
    &:hover {
      background: linear-gradient(180deg, #4a4a4a 0%, #333 100%);
      border-color: #f5a623;
    }
    &:active {
      background: linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%);
      box-shadow: inset 1px 1px 2px rgba(0,0,0,0.5);
    }
    &--glow {
      animation: cart-glow 2s ease-in-out infinite;
    }
  }
  @keyframes cart-glow {
    0%, 100% { border-color: #555; color: #f5a623; }
    50% { border-color: #f5a623; color: #ffcc66; box-shadow: 0 0 6px rgba(245, 166, 35, 0.3); }
  }
  .binco-header__account-btn {
    background: linear-gradient(180deg, #3a3a3a 0%, #222 100%);
    border: 1px solid #555;
    color: #999;
    padding: 4px 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 1px 1px rgba(255,255,255,0.08), 1px 1px 2px rgba(0,0,0,0.3);
    &:hover {
      background: linear-gradient(180deg, #4a4a4a 0%, #333 100%);
      border-color: #f5a623;
      color: #f5a623;
    }
    &:active {
      background: linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%);
      box-shadow: inset 1px 1px 2px rgba(0,0,0,0.5);
    }
  }
  /* Search Overlay */
  .binco-search-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 50;
    display: flex;
    flex-direction: column;
  }
  .binco-search-overlay__bg {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(6px) brightness(0.3);
    transform: scale(1.05);
  }
  .binco-search-overlay__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-top: 58px;
    overflow: hidden;
  }
  .binco-search-overlay__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
  }
  .binco-search-overlay__section {
    font-size: 9px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    margin-bottom: 8px;
    margin-top: 8px;
  }
  .binco-search-overlay__brands {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }
  .binco-search-overlay__brand-tag {
    background: #2a2a2a;
    border: 1px solid #444;
    color: #ccc;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    padding: 4px 10px;
    cursor: pointer;
    font-weight: bold;
    &:hover { color: #f5a623; border-color: #f5a623; }
  }
  .binco-search-overlay__cat {
    color: #666;
    font-size: 9px;
    margin-left: 4px;
    font-weight: normal;
  }
  .binco-search-overlay__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }
  .binco-search-overlay__empty {
    color: #555;
    text-align: center;
    padding: 40px;
    font-size: 14px;
  }

  .binco-search-clear {
    position: absolute;
    right: calc(50% - 145px);
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #666;
    font-size: 12px;
    cursor: pointer;
    padding: 2px 6px;
    &:hover { color: #f5a623; }
  }
  .binco-search-dropdown__section {
    padding: 6px 12px 3px;
    font-size: 8px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    border-bottom: 1px solid #2a2a2a;
  }
  .binco-search-dropdown__item--brand {
    font-weight: bold;
  }
  .binco-search-dropdown {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 300px;
    background: #222;
    border: 1px solid #444;
    z-index: 20;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .binco-search-dropdown__item {
    padding: 8px 12px;
    font-size: 11px;
    color: #ccc;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #2a2a2a;
    &:last-child { border-bottom: none; }
    &:hover { background: rgba(245, 166, 35, 0.08); color: #f5a623; }
  }
  .binco-search-dropdown__cat {
    font-size: 9px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .binco-header__nav {
    display: flex;
    gap: 14px;
    flex-shrink: 0;
    a {
      color: #999;
      text-decoration: none;
      font-size: 10px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      &:hover { color: #f5a623; text-decoration: underline; }
    }
  }

  /* ── Body ── */
  .binco-body {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
  }

  /* ── Sidebar ── */
  .binco-sidebar {
    width: 130px;
    min-width: 130px;
    background: #222;
    border-right: 1px solid #333;
    padding: 6px 0;
    display: flex;
    flex-direction: column;
    overflow: visible;
    position: relative;
    z-index: 10;
  }
  .binco-sidebar__item-wrap { position: relative; }
  .binco-sidebar__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 9px 14px;
    background: none;
    border: none;
    border-bottom: 1px solid #2a2a2a;
    color: #aaa;
    font-size: 11px;
    font-weight: bold;
    text-align: left;
    cursor: pointer;
    font-family: Verdana, Tahoma, sans-serif;
    text-transform: uppercase;
    letter-spacing: 1px;
    &:hover { color: #f5a623; background: rgba(245, 166, 35, 0.06); }
    &--active { color: #f5a623; background: rgba(245, 166, 35, 0.1); border-left: 3px solid #f5a623; }
  }
  .binco-sidebar__arrow { font-size: 8px; color: #666; }
  .binco-submenu {
    position: absolute;
    left: 100%;
    top: 0;
    background: #222;
    border: 1px solid #444;
    min-width: 160px;
    z-index: 10;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
    &--multi {
      display: flex;
    }
  }
  .binco-submenu__col {
    min-width: 160px;
    & + & { border-left: 1px solid #333; }
  }
  .binco-submenu__item {
    display: block;
    width: 100%;
    padding: 7px 14px;
    background: none;
    border: none;
    border-bottom: 1px solid #2a2a2a;
    color: #bbb;
    font-size: 11px;
    text-align: left;
    cursor: pointer;
    font-family: Verdana, Tahoma, sans-serif;
    &:last-child { border-bottom: none; }
    &:hover { color: #f5a623; background: rgba(245, 166, 35, 0.1); }
  }
  .binco-submenu__letter {
    padding: 4px 14px 2px;
    font-size: 9px;
    font-weight: 700;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1.5px;
  }
  .binco-sidebar__help {
    margin-top: auto;
    padding: 12px 14px;
    font-size: 10px;
    color: #555;
    line-height: 1.6;
    a { color: #f5a623; text-decoration: none; &:hover { text-decoration: underline; } }
  }
  .binco-sidebar__heading {
    padding: 10px 14px 4px;
    font-size: 8px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-top: 6px;
  }
  .binco-sidebar__sep { height: 1px; background: #333; margin: 12px 14px; }

  /* ── Content (Home) ── */
  .binco-content {
    flex: 1;
    overflow-y: auto;
    padding: 10px 14px;
    background-color: #1a1a1a;
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    position: relative;
    &::before {
      content: '';
      position: sticky;
      top: 0;
      display: block;
      width: 100%;
      height: 0;
      box-shadow: 0 0 0 100vmax rgba(26,26,26,0.85);
      pointer-events: none;
      z-index: 0;
    }
    &::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background-image: url(${overlaygrit});
      background-size: cover;
      background-position: center;
      mix-blend-mode: screen;
      opacity: 0.05;
      pointer-events: none;
      z-index: 0;
    }
    & > * {
      position: relative;
      z-index: 1;
    }
  }
  /* ── Featured Slideshow ── */
  .featured-slideshow {
    overflow: hidden;
    position: relative;
    margin-bottom: 12px;
    &:hover .featured-slideshow__arrow { opacity: 1; }
  }
  .featured-slideshow__arrow {
    position: absolute;
    top: 115px;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid #555;
    color: #ccc;
    font-size: 14px;
    padding: 8px 6px;
    cursor: pointer;
    z-index: 5;
    opacity: 0;
    transition: opacity 0.2s;
    &:hover { color: #f5a623; border-color: #f5a623; background: rgba(0, 0, 0, 0.7); }
    &--left { left: 4px; }
    &--right { right: 4px; }
  }
  .featured-slideshow__slider {
    display: flex;
    width: 200%;
    transition: none;
    &--sliding {
      transition: transform 0.4s ease-in-out;
      transform: translateX(-50%);
    }
  }
  .featured-slideshow__page {
    width: 50%;
    display: grid;
    gap: 10px;
    flex-shrink: 0;
    .product-card {
      min-width: 0;
      max-width: 200px;
    }
    .product-card .product-card__image {
      width: 100%;
      height: 200px;
      padding-bottom: 0;
    }
    .product-card .product-card__image img,
    .product-card .product-card__image .product-card__img-front,
    .product-card .product-card__image .product-card__img-back {
      position: absolute;
      top: 0; left: 0;
      width: 100%;
      height: 200px;
      object-fit: contain;
    }
  }
  .featured-slideshow__indicators {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin-top: 10px;
  }
  .featured-slideshow__dash {
    width: 40px;
    height: 3px;
    background: #333;
    cursor: pointer;
    overflow: hidden;
    border-radius: 1px;
  }
  .featured-slideshow__dash-fill {
    height: 100%;
    background: #f5a623;
    transition: width 0.05s linear;
  }

  /* ── Loading ── */
  .binco-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
  }
  .binco-loading__bar {
    width: 200px;
    height: 6px;
    background: #222;
    border: 1px solid #333;
    border-radius: 2px;
    overflow: hidden;
  }
  .binco-loading__fill {
    height: 100%;
    width: 40%;
    background: #f5a623;
    animation: binco-load 1.2s ease-in-out infinite;
  }
  @keyframes binco-load {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
  .binco-loading__text {
    color: #666;
    font-size: 11px;
  }

  /* ── Carousel ── */
  .binco-carousel {
    overflow: hidden;
    margin: 8px 0 12px;
    border-top: 1px solid #2a2a2a;
    border-bottom: 1px solid #2a2a2a;
  }
  .binco-carousel__track {
    display: flex;
    will-change: transform;
  }
  .binco-carousel__slide {
    flex-shrink: 0;
    height: 220px;
    opacity: 0.7;
    img {
      height: 100%;
      width: auto;
      display: block;
    }
  }

  .binco-content__banner {
    text-align: center;
    color: #f5a623;
    font-size: 11px;
    padding: 6px;
    margin-bottom: 8px;
    font-weight: bold;
  }
  .binco-content__heading {
    color: #f5a623;
    font-weight: 900;
    font-size: 14px;
    margin-bottom: 10px;
    margin-top: 8px;
    &--sticky {
      position: sticky;
      top: -10px;
      background: #1a1a1a;
      padding: 14px 0 4px;
      margin: 0 -14px;
      padding-left: 14px;
      z-index: 3;
      border-bottom: 1px solid rgba(245, 166, 35, 0.4);
    }
  }
  .binco-content__heading--toggle {
    cursor: pointer;
    user-select: none;
    &:hover { opacity: 0.8; }
  }
  .binco-content__collapse-arrow {
    font-size: 10px;
    margin-left: 6px;
    color: #888;
  }
  .binco-content__heading--sortbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .binco-sortbar {
    display: flex;
    gap: 4px;
  }
  .binco-sortbar__btn {
    background: none;
    border: 1px solid #444;
    color: #888;
    font-size: 9px;
    font-family: Tahoma, sans-serif;
    padding: 2px 8px;
    cursor: pointer;
    &:hover { color: #f5a623; border-color: #f5a623; }
    &--active { color: #f5a623; border-color: #f5a623; }
  }
  .binco-content__divider {
    height: 1px;
    background: linear-gradient(to right, transparent, #f5a623, transparent);
    margin: 16px 0 8px;
    opacity: 0.4;
  }
  .binco-content__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 16px;
  }
  .binco-content__empty {
    text-align: center;
    color: #555;
    padding: 40px;
    font-size: 12px;
  }
  .product-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    padding: 8px;
    border: 1px solid transparent;
    &:hover { border-color: #f5a623; background: rgba(245, 166, 35, 0.04); }
    img { -webkit-user-drag: none; user-select: none; }
  }
  .product-card__image {
    width: 100%;
    height: 0;
    padding-bottom: 130%;
    overflow: hidden;
    background: #111;
    border: 1px solid #333;
    position: relative;
    .product-card__img-front { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; }
    img { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; }
    .product-card__img-back {
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      object-fit: contain;
      opacity: 0;
      transition: opacity 0.1s ease;
    }
    &--dual:hover .product-card__img-back { opacity: 1; }
    &--dual::after {
      content: '⇆ DUAL';
      position: absolute;
      top: 6px;
      right: 6px;
      background: rgba(0,0,0,0.6);
      color: #f5a623;
      font-size: 9px;
      padding: 3px 6px;
      border-radius: 2px;
      letter-spacing: 0.5px;
      pointer-events: none;
      z-index: 1;
    }
  }
  .product-card__name {
    color: #999;
    text-decoration: underline;
    text-align: center;
    font-size: 10px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
    &:hover { color: #f5a623; }
  }
  .product-card__price {
    color: #eee;
    font-weight: bold;
    font-size: 11px;
  }

  /* ── Category View (Plex-style) ── */
  .cat-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    position: relative;
  }

  /* Top bar */
  .cat-view__topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    background: #1e1e1e;
    border-bottom: 1px solid #333;
    flex-shrink: 0;
    position: relative;
    z-index: 5;
  }
  .cat-view__dropdown-wrap {
    position: relative;
  }
  .cat-view__dropdown-btn {
    background: none;
    border: none;
    color: #e0e0e0;
    font-size: 14px;
    font-weight: bold;
    font-family: Verdana, Tahoma, sans-serif;
    cursor: pointer;
    padding: 2px 6px;
    &:hover { color: #f5a623; }
  }
  .cat-view__count {
    color: #666;
    font-size: 11px;
  }
  .cat-view__overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 4;
  }
  .cat-view__dropdown-panel {
    position: absolute;
    top: 100%;
    left: 0;
    background: #252525;
    border: 1px solid #444;
    z-index: 6;
    min-width: 400px;
    max-width: 600px;
    box-shadow: 2px 4px 12px rgba(0,0,0,0.6);
    padding: 10px;
  }
  .cat-view__dropdown-header {
    color: #f5a623;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 2px;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #333;
  }
  .cat-view__dropdown-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
  }
  .cat-view__dropdown-tag {
    background: none;
    border: none;
    color: #bbb;
    font-size: 11px;
    font-family: Verdana, Tahoma, sans-serif;
    cursor: pointer;
    padding: 3px 0;
    white-space: nowrap;
    &:hover { color: #f5a623; }
    &--active { color: #f5a623; }
  }
  .cat-view__genre-btn {
    background: none;
    border: 1px solid #444;
    color: #bbb;
    font-size: 11px;
    font-family: Verdana, Tahoma, sans-serif;
    cursor: pointer;
    padding: 2px 8px;
    &:hover { color: #f5a623; border-color: #f5a623; }
  }
  .cat-view__filter-tag--genre {
    background: rgba(100, 180, 255, 0.15);
    border-color: #6ab4ff;
    color: #6ab4ff;
    .cat-view__filter-tag-remove { color: #6ab4ff; }
  }
  .cat-view__active-filters {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }
  .cat-view__filter-tag {
    display: flex;
    align-items: center;
    gap: 3px;
    background: rgba(245, 166, 35, 0.15);
    border: 1px solid #f5a623;
    color: #f5a623;
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 1px;
    white-space: nowrap;
  }
  .cat-view__filter-tag-remove {
    background: none;
    border: none;
    color: #f5a623;
    font-size: 9px;
    cursor: pointer;
    padding: 0 1px;
    opacity: 0.7;
    &:hover { opacity: 1; }
  }
  .cat-view__filter-clear {
    background: none;
    border: none;
    color: #666;
    font-size: 9px;
    cursor: pointer;
    padding: 0 4px;
    &:hover { color: #f5a623; }
  }

  /* Tabs */
  .cat-view__tabs {
    margin-left: auto;
    display: flex;
    gap: 4px;
  }
  .cat-view__tab {
    background: none;
    border: none;
    color: #888;
    font-size: 11px;
    font-family: Verdana, Tahoma, sans-serif;
    cursor: pointer;
    padding: 4px 10px;
    border-bottom: 2px solid transparent;
    &:hover { color: #ccc; }
    &--active {
      color: #f5a623;
      border-bottom-color: #f5a623;
    }
  }

  /* Body: grid + alphabet rail */
  .cat-view__body {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  /* Scrollable grid area */
  .cat-view__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 8px 10px;
  }
  .cat-view__section {
    margin-bottom: 12px;
  }
  .cat-view__section-label {
    font-size: 14px;
    font-weight: 900;
    color: #f5a623;
    font-family: 'Arial Black', Tahoma, sans-serif;
    padding: 2px 0 6px;
    border-bottom: 1px solid #2a2a2a;
    margin-bottom: 8px;
  }
  .cat-view__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 14px;
  }
  .cat-view__card {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    &:hover .cat-view__card-image {
      border-color: #f5a623;
    }
  }
  .cat-view__card-image {
    width: 100%;
    height: 0;
    padding-bottom: 133%;
    position: relative;
    overflow: hidden;
    background: #111;
    border: 2px solid transparent;
    border-radius: 2px;
    transition: border-color 0.12s;
    .cat-view__img-front { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; transition: opacity 0.1s ease; }
    .cat-view__img-back { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; opacity: 0; transition: opacity 0.1s ease; }
    img { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; }
    &--dual:hover .cat-view__img-back { opacity: 1; }
    &--dual::after {
      content: '⇆ DUAL';
      position: absolute;
      top: 6px;
      right: 6px;
      background: rgba(0,0,0,0.6);
      color: #f5a623;
      font-size: 9px;
      padding: 3px 6px;
      border-radius: 2px;
      letter-spacing: 0.5px;
      pointer-events: none;
      z-index: 1;
    }
  }
  .cat-view__card-name {
    font-size: 11px;
    color: #ddd;
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cat-view__card-price {
    font-size: 10px;
    color: #888;
  }

  /* Flat grid (no sections) */
  .cat-view__flatgrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 14px;
  }

  /* View toggle */
  .cat-view__viewtoggle {
    display: flex;
    gap: 2px;
    margin-left: 8px;
  }
  .cat-view__viewbtn {
    background: #252525;
    border: 1px solid #444;
    color: #666;
    font-size: 13px;
    width: 24px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    &:hover { color: #ccc; background: #333; }
    &--active { color: #f5a623; border-color: #f5a623; background: #2a2a2a; }
  }

  /* Alphabet rail (right side, like Plex) */
  .cat-view__alpha {
    display: flex;
    flex-direction: column;
    width: 22px;
    min-width: 22px;
    background: transparent;
    padding: 1px 0;
    justify-content: flex-start;
  }
  .cat-view__alpha-letter {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #777;
    font-size: 11px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    padding: 1px 0;
    line-height: 1;
    &:hover:not(:disabled) {
      color: #f5a623;
    }
    &--empty {
      color: #333;
      cursor: default;
    }
  }

  /* ── Payment Badges ── */
  .binco-payment-badges {
    position: absolute;
    bottom: 6px;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 14px;
    opacity: 0.12;
    pointer-events: none;
    z-index: 2;
    img {
      height: 22px;
      width: auto;
      filter: invert(1) brightness(2);
    }
  }

  /* ── Status Bar ── */
  .binco-statusbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 20px;
    padding: 0 8px;
    background: #222;
    border-top: 1px solid #333;
    font-size: 10px;
    color: #777;
    flex-shrink: 0;
  }
`;

export default Binco;
